import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { cartonService } from '../../services/cartonService';
import { Package, Plus, Edit2, Trash2, AlertTriangle, Search, RefreshCw, X, Save } from 'lucide-react';

const CARTON_TYPES = ['Petit', 'Moyen', 'Grand', 'XL', 'Personnalisé'];

const defaultForm = {
  cartonType: '',
  dimensions: '',
  description: '',
  stockQuantity: 0,
  minStockLevel: 10,
  unitPriceBuy: 0,
  unitPriceSell: 0,
  supplierId: '',
  location: 'Entrepôt principal',
  notes: ''
};

export default function CartonInventoryManagement() {
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustQty, setAdjustQty] = useState(0);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inv, sup] = await Promise.all([
        cartonService?.getCartonInventory(),
        cartonService?.getCartonSuppliers()
      ]);
      setInventory(inv);
      setSuppliers(sup);
    } catch (e) {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = inventory?.filter(item =>
    item?.cartonType?.toLowerCase()?.includes(search?.toLowerCase()) ||
    item?.location?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const openAdd = () => {
    setEditItem(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      cartonType: item?.cartonType || '',
      dimensions: item?.dimensions || '',
      description: item?.description || '',
      stockQuantity: item?.stockQuantity || 0,
      minStockLevel: item?.minStockLevel || 10,
      unitPriceBuy: item?.unitPriceBuy || 0,
      unitPriceSell: item?.unitPriceSell || 0,
      supplierId: item?.supplierId || '',
      location: item?.location || 'Entrepôt principal',
      notes: item?.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form?.cartonType) return;
    setSaving(true);
    try {
      if (editItem) {
        await cartonService?.updateCartonInventoryItem(editItem?.id, form);
      } else {
        await cartonService?.createCartonInventoryItem(form);
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await cartonService?.deleteCartonInventoryItem(id);
      fetchData();
    } catch (e) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleAdjust = async () => {
    if (!adjustModal || adjustQty === 0) return;
    try {
      await cartonService?.adjustCartonStock(adjustModal?.id, parseInt(adjustQty));
      setAdjustModal(null);
      setAdjustQty(0);
      fetchData();
    } catch (e) {
      setError('Erreur lors de l\'ajustement');
    }
  };

  const totalStock = inventory?.reduce((sum, i) => sum + (i?.stockQuantity || 0), 0);
  const lowStock = inventory?.filter(i => (i?.stockQuantity || 0) <= (i?.minStockLevel || 10));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Package className="h-7 w-7 text-amber-600" />
              Inventaire Cartons
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Gestion du stock de cartons vides</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
              <Plus className="h-4 w-4" /> Nouveau type
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Types de cartons</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{inventory?.length}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Stock total</p>
            <p className="text-2xl font-bold text-amber-600">{totalStock}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Stock faible</p>
            <p className="text-2xl font-bold text-red-500">{lowStock?.length}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un type de carton..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
          />
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Chargement...</div>
          ) : filtered?.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Aucun article trouvé</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Type</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Dimensions</th>
                    <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Stock</th>
                    <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Min.</th>
                    <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Prix achat</th>
                    <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Prix vente</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Emplacement</th>
                    <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filtered?.map(item => (
                    <tr key={item?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 dark:text-white">{item?.cartonType}</span>
                        {item?.description && <p className="text-xs text-gray-500 dark:text-gray-400">{item?.description}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item?.dimensions || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${(item?.stockQuantity || 0) <= (item?.minStockLevel || 10) ? 'text-red-500' : 'text-green-600'}`}>
                          {item?.stockQuantity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{item?.minStockLevel}</td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{item?.unitPriceBuy?.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-amber-600 font-medium">{item?.unitPriceSell?.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{item?.location}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => { setAdjustModal(item); setAdjustQty(0); }}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                            title="Ajuster le stock"
                          >
                            ±
                          </button>
                          <button onClick={() => openEdit(item)} className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded">
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDelete(item?.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editItem ? 'Modifier le type' : 'Nouveau type de carton'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
                  <select
                    value={form?.cartonType}
                    onChange={e => setForm(f => ({ ...f, cartonType: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Sélectionner...</option>
                    {CARTON_TYPES?.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dimensions</label>
                  <input type="text" placeholder="ex: 40x30x30 cm" value={form?.dimensions} onChange={e => setForm(f => ({ ...f, dimensions: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input type="text" value={form?.description} onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stock actuel</label>
                  <input type="number" min="0" value={form?.stockQuantity} onChange={e => setForm(f => ({ ...f, stockQuantity: parseInt(e?.target?.value) || 0 }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stock minimum</label>
                  <input type="number" min="0" value={form?.minStockLevel} onChange={e => setForm(f => ({ ...f, minStockLevel: parseInt(e?.target?.value) || 0 }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prix achat (€)</label>
                  <input type="number" min="0" step="0.01" value={form?.unitPriceBuy} onChange={e => setForm(f => ({ ...f, unitPriceBuy: parseFloat(e?.target?.value) || 0 }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prix vente (€)</label>
                  <input type="number" min="0" step="0.01" value={form?.unitPriceSell} onChange={e => setForm(f => ({ ...f, unitPriceSell: parseFloat(e?.target?.value) || 0 }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fournisseur</label>
                <select value={form?.supplierId} onChange={e => setForm(f => ({ ...f, supplierId: e?.target?.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                  <option value="">Aucun</option>
                  {suppliers?.map(s => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Emplacement</label>
                <input type="text" value={form?.location} onChange={e => setForm(f => ({ ...f, location: e?.target?.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea value={form?.notes} onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))} rows={2}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-slate-700">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving || !form?.cartonType}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Adjust Stock Modal */}
      {adjustModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-sm shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Ajuster le stock — {adjustModal?.cartonType}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Stock actuel : <strong className="text-gray-900 dark:text-white">{adjustModal?.stockQuantity}</strong>
            </p>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ajustement (+ pour ajouter, - pour retirer)
            </label>
            <input type="number" value={adjustQty} onChange={e => setAdjustQty(e?.target?.value)}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setAdjustModal(null)} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300">
                Annuler
              </button>
              <button onClick={handleAdjust} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
